import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import {
  defaultCO2EmissionsOptions,
  defaultVehicleTypeOptions,
} from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { Form } from 'antd';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

export type VehicleFormValues = {
  value: string;
  startDate: string;
  co2Emissions: Option<string> | null;
  originalMarketValue: string;
  type: Option<string> | null;
  description: string;
  endDate: string;
  totalKilometres: string;
  eeContribution: string;
  totalEeContribution: string;
};

export type VehicleFormRef = {
  submit: () => void;
  setFieldsValue: (values: Partial<VehicleFormValues>) => void;
};

type VehicleFormProps = {
  formRef?: Ref<VehicleFormRef>;
  onSubmit?: (values: VehicleFormValues) => void;
  defaultValues?: VehicleFormValues;
};

const VehicleForm: React.FC<VehicleFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const { messages } = useTypedIntl();

  const [form] = Form.useForm<VehicleFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    setFieldsValue(values) {
      form.setFieldsValue(values);
    },
  }));

  const onFinish = (values: VehicleFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={defaultValues}
    >
      <Form.Item
        label={messages['common.type']}
        name="type"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppSelect
          options={defaultVehicleTypeOptions}
          placeholder={messages['form.select']}
        />
      </Form.Item>
      <Form.Item label={messages['common.description']} name="description">
        <AppInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.originalMarketValue']}
        name="originalMarketValue"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.co2Emissions']}
        name="co2Emissions"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppSelect
          options={defaultCO2EmissionsOptions}
          placeholder={messages['form.select']}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.startDate']}
        name="startDate"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppDatePicker
          format="DD/MM/YYYY"
          placeholder={messages['form.selectDate']}
        />
      </Form.Item>
      <Form.Item label={messages['common.endDate']} name="endDate">
        <AppDatePicker
          format="DD/MM/YYYY"
          placeholder={messages['form.selectDate']}
        />
      </Form.Item>
      <Form.Item label={messages['common.kilometresInTheWeek']} name="value">
        <AppRateInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.totalKilometres']}
        name="totalKilometres"
      >
        <AppRateInput placeholder={messages['form.enter']} disabled />
      </Form.Item>
      <Form.Item
        label={messages['common.employeeContributionInTheWeek']}
        name="eeContribution"
      >
        <AppCurrencyInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.totalEmployeeContributionInTheWeek']}
        name="totalEeContribution"
      >
        <AppCurrencyInput placeholder={messages['form.enter']} disabled />
      </Form.Item>
    </AppFormContainer>
  );
};

export default VehicleForm;
